// src/components/student/VideoUploadPage.js
//
// 3-step video upload with: sport-specific focus areas, coach selector, video compression.

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { createNotification } from '../../utils/notifications';
import { collection, doc } from 'firebase/firestore';
import { createVideoUploadShell, markVideoUploadFailed, finalizeVideoUpload } from '../../utils/videoUpload';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../AuthContext';
import { useSelectedOrganizationContext } from '../../utils/organizationSelection';
import { SPORT_FOCUS_AREAS, getFocusAreasForLevel } from '../../utils/sportFocusAreas';
import { compressVideo, canCompress, formatFileSize } from '../../utils/videoCompression';
import StudentNav from '../shared/StudentNav';
import StudentOrgBar from './StudentOrgBar';
import { useStudentContext } from '../../hooks/useStudentContext';
import MotusButton from '../shared/MotusButton';
import MotusSpinner from '../shared/MotusSpinner';
import MotusIcon from '../shared/MotusIcon';

const C = {
  bg:'#FAFAF8', card:'#FFFFFF', card2:'#F5F4F0', card3:'#ECEAE4',
  border:'rgba(0,0,0,0.06)', borderH:'rgba(0,0,0,0.12)',
  blue:'#2563EB', blueDim:'rgba(37,99,235,0.08)',
  green:'#22C55E', greenDim:'rgba(34,197,94,0.08)',
  orange:'#F05A1F', orangeDim:'rgba(240,90,31,0.08)',
  purple:'#9333EA', gold:'#D97706', goldDim:'rgba(217,119,6,0.08)',
  t1:'#1C1B18', t2:'#6B6B63', t3:'#A3A299',
};
const font = "'DM Sans',sans-serif";
const fontD = "'Bebas Neue',sans-serif";

function VideoUploadPage() {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  // Pass 72 (§22/§23) — resolve the CANONICAL selected/current organization (not the raw legacy userData.orgId),
  // and stamp it on the video at creation so a new real-coach upload is tenant-scoped from its first write.
  const { selectedOrganization } = useSelectedOrganizationContext(userData);
  // Pass 85.1A.2 (§9–§13) — upload identity comes from the CANONICAL ACCESS LANE and FAILS CLOSED until it is known:
  //   MODERN → org + participantId MUST both come from the canonical context (NO fallback to selectedOrganization /
  //            users.orgId); an upload cannot begin until both are non-empty.
  //   LEGACY → the existing org-only compatibility selection (selected/legacy org), no participantId.
  //   loading / error / needs_org / ambiguous → BLOCKED: no shell, no Storage object, no notification, no partial attempt.
  const studentCtx = useStudentContext(currentUser?.uid || '');
  const lane = studentCtx.lane;
  const isModernUpload = lane === 'modern';
  const isLegacyUpload = lane === 'legacy';
  const orgId = isModernUpload
    ? (studentCtx.selectedOrganizationId || '')
    : (isLegacyUpload ? (selectedOrganization?.organizationId || userData?.orgId || '') : '');
  const participantId = isModernUpload ? (studentCtx.activeParticipantId || '') : '';
  // The ONLY states in which an upload may start: a fully-resolved modern identity, or a confirmed legacy org.
  const uploadReady = (isModernUpload && !!orgId && !!participantId) || (isLegacyUpload && !!orgId);

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [compressedFile, setCompressedFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [compressionSavings, setCompressionSavings] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  // Step 2 form
  const [title, setTitle] = useState('');
  const [sport, setSport] = useState('ski');
  const [notes, setNotes] = useState('');
  const [focusTags, setFocusTags] = useState([]);
  const [difficulty, setDifficulty] = useState('Moderate');
  const [selectedCoach, setSelectedCoach] = useState('any');
  const [submitting, setSubmitting] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);

  const fileInputRef = useRef(null);
  // Pass 72 (§9) — ONE stable videoId per submission attempt, generated once and reused for Firestore + Storage
  // + retry + finalization (was a fresh addDoc auto-id on every attempt).
  const videoIdRef = useRef('');
  const uploadedUrlRef = useRef('');
  const lastFileRef = useRef(null);

  // Pass 72 (§24-26) — the specific-coach picker is DEFERRED: a student cannot securely read the org's instructor
  // roster (organizationMembers is staff-only) and 45/46 staff are not yet Auth-linked, so a validated org-scoped
  // picker needs infrastructure beyond this pass. Uploads go to the org's shared review queue ("First available
  // coach"); an operator can assign a specific coach, and the validated assignment path exists in Rules for a
  // future picker. So there is no client roster query here.

  // Pass 78 — the legacy quickCapture progression cache (which fabricated a "current level" default of intermediate)
  // is removed. Focus areas are the NEUTRAL, sport-appropriate menu grouped by level, so the student chooses what to
  // get feedback on WITHOUT the app claiming a demonstrated level it has not established (Pass-77 truth). The honest
  // demonstrated level lives on /me/progress.
  const focusAreas = getFocusAreasForLevel(sport, 'expert');

  // Group focus areas by level for display
  const groupedAreas = {};
  focusAreas.forEach(a => {
    if (!groupedAreas[a.levelLabel]) groupedAreas[a.levelLabel] = [];
    groupedAreas[a.levelLabel].push(a);
  });

  // ── File handling with compression ──
  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile) return;
    // Pass 85.1A.2 (§14) — FAIL CLOSED on the file-selection race: if the canonical identity mode isn't resolved yet,
    // do NOT compress/upload. `uploadFile` reports the correct lane state and starts NO shell / NO Storage object.
    if (!uploadReady) { uploadFile(selectedFile); return; }
    setFile(selectedFile);
    setVideoPreview(URL.createObjectURL(selectedFile));
    setTitle(selectedFile.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' '));

    // Compress first, then upload
    if (canCompress() && selectedFile.size > 10 * 1024 * 1024) {
      setCompressing(true);
      setCompressionProgress(0);
      setUploadStatus('Compressing video…');
      try {
        const result = await compressVideo(selectedFile, setCompressionProgress);
        setCompressedFile(result.file);
        if (result.ratio < 1) {
          setCompressionSavings({ original: result.originalSize, compressed: result.compressedSize, ratio: result.ratio });
        }
        setCompressing(false);
        uploadFile(result.file);
      } catch (_) {
        setCompressing(false);
        uploadFile(selectedFile);
      }
    } else {
      uploadFile(selectedFile);
    }
  };

  const uploadFile = async (fileToUpload) => {
    if (!currentUser?.uid) return;
    // Pass 85.1A.2 (§9–§11) — FAIL CLOSED until the canonical identity mode is known. While the participant context is
    // loading / errored / needs an org choice / is ambiguous, an upload MUST NOT begin: no shell, no Storage object, no
    // notification. A modern upload also requires BOTH org AND participantId; a legacy upload requires an org.
    if (!uploadReady) {
      setUploadStatus(
        lane === 'loading' ? 'Resolving your coaching profile…'
          : lane === 'error' ? 'We could not load your coaching identity. Please try again before uploading.'
          : lane === 'needs_org' ? 'Choose your organisation above before uploading.'
          : lane === 'ambiguous' ? 'Your account is linked to more than one profile in this organisation — your coach must resolve this before you can upload.'
          : 'Select an organisation before uploading for coach review.',
      );
      setUploadFailed(true);
      setUploading(false);
      return;
    }
    lastFileRef.current = fileToUpload;
    setUploading(true);
    setUploadFailed(false);
    setUploadProgress(0);
    setUploadStatus('Uploading…');

    // ONE stable videoId per submission attempt — generated once, reused across retries + finalize.
    if (!videoIdRef.current) videoIdRef.current = doc(collection(db, 'videos')).id;
    const videoId = videoIdRef.current;

    const failDurably = async (err) => {
      setUploadStatus('Upload failed.');
      setUploadFailed(true);
      setUploading(false);
      try { await markVideoUploadFailed(db, { videoId, actorUserId: currentUser.uid, uploadError: err }); } catch (_) {}
    };

    try {
      // Create the tenant-stamped shell ONCE (idempotent on retry — the same id is reused).
      await createVideoUploadShell(db, {
        videoId,
        actorUserId: currentUser.uid,
        organizationId: orgId,
        participantId, // Pass 85.1A.1 — durable modern coaching identity (blank for a legacy account)
        sport,
        videoName: (title || fileToUpload.name || 'Untitled Video').replace(/\.[^/.]+$/, ''),
      });

      const safeName = (fileToUpload.name || 'video').replace(/[^\w.-]+/g, '_');
      const storageRef = ref(storage, `videos/${currentUser.uid}/${videoId}/${safeName}`);
      const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

      uploadTask.on('state_changed',
        (snapshot) => {
          const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(pct);
          if (pct < 30) setUploadStatus('Uploading…');
          else if (pct < 70) setUploadStatus('Processing…');
          else if (pct < 95) setUploadStatus('Finalising…');
          else setUploadStatus('Almost there…');
        },
        (err) => { failDurably(err); },   // §17 — persist a DURABLE failed state, not just local UI
        async () => {
          try {
            // §13 — the Storage URL exists, but the video stays uploadStatus:'uploading' (NOT claimable) until
            // the student finalizes details + coach choice. We hold the URL and advance to the details step.
            uploadedUrlRef.current = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadStatus('✓ Upload complete!');
            setUploading(false);
            setTimeout(() => setStep(2), 600);
          } catch (err) { await failDurably(err); }
        }
      );
    } catch (err) { await failDurably(err); }
  };

  const retryUpload = () => { if (lastFileRef.current) uploadFile(lastFileRef.current); };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setUploadStatus('');
    try {
      const videoId = videoIdRef.current;
      const videoURL = uploadedUrlRef.current;
      if (!videoId || !videoURL) throw new Error('Your upload did not finish. Please re-upload the video.');

      // §14 — ONE atomic finalization: URL + uploadStatus:'complete' + details (+ specific coach, when a
      // validated picker exists). "First available coach" leaves it unassigned → enters the org queue only NOW.
      const coachAuthUserId = selectedCoach && selectedCoach !== 'any' ? selectedCoach : '';
      await finalizeVideoUpload(db, {
        videoId,
        actorUserId: currentUser.uid,
        videoURL,
        videoName: title || file?.name || 'Untitled Video',
        sport,
        studentNotes: notes || '',
        focusTags,
        difficulty,
        organizationId: orgId,
        coachAuthUserId,
      });

      // Pass 72A (§14-§16) — DURABLE, deterministic idempotency: a fixed notificationId keyed on the videoId
      // means an uncertain-result finalize retry (reload / second device / delayed) writes the SAME notification
      // document, never a duplicate — and it recovers a notification the first attempt failed to create. Called
      // unconditionally (incl. the finalize `alreadyFinal` no-op path) because the deterministic id converges to one.
      createNotification(db, {
        userId: currentUser.uid,
        type: 'video_uploaded',
        notificationId: `video_uploaded__${videoId}`,
        title: 'Video uploaded successfully',
        message: `"${title || file?.name || 'Your video'}" is now waiting for coach review.`,
        link: '/dashboard/videos',
        relatedEntityType: 'video',
        relatedEntityId: videoId,
      }).catch(() => {});

      setStep(3);
    } catch (err) {
      // §40/§41 — never a false success: surface the failure and keep the student on the details step to retry.
      setUploadStatus(err?.message || 'We could not submit this video. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTag = (key) => setFocusTags(p => p.includes(key) ? p.filter(t => t !== key) : [...p, key]);
  const uploadingFile = compressedFile || file;
  const fileSizeDisplay = uploadingFile ? formatFileSize(uploadingFile.size) : '';

  return (
    <div style={{ minHeight:'100vh', background:C.bg, color:C.t1, fontFamily:font }}>
      <div style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0, background:'radial-gradient(ellipse 60% 40% at 5% 0%,rgba(37,99,235,0.03),transparent 55%)' }} />

      <StudentNav activeTab="videos" />

      <main style={{ padding:'36px 36px 80px', maxWidth:960, margin:'0 auto', position:'relative', zIndex:1 }} data-student-mode={lane} data-upload-ready={uploadReady ? 'yes' : 'no'}>

        <StudentOrgBar ctx={studentCtx} />
        {(lane === 'loading' || lane === 'error') && (
          <div data-testid="upload-lane-state" style={{ background: lane === 'error' ? '#FEF2F2' : '#F5F4F0', border: `1px solid ${lane === 'error' ? '#FECACA' : 'rgba(0,0,0,0.08)'}`, color: lane === 'error' ? '#B91C1C' : '#6B6B63', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:13 }}>
            {lane === 'loading' ? 'Resolving your coaching profile…' : 'We couldn’t load your coaching identity. Please try again before uploading.'}
          </div>
        )}

        {/* STEP BAR */}
        <div style={{ display:'flex', alignItems:'center', marginBottom:32 }}>
          <StepDot n={1} s={step} /> <StepLine done={step>1} active={step===1} />
          <StepDot n={2} s={step} /> <StepLine done={step>2} active={step===2} />
          <StepDot n={3} s={step} />
        </div>

        {/* XP STRIP */}
        <div style={{ background:'linear-gradient(135deg,#FFF8F0,#FFF3E6)', border:'1px solid rgba(217,119,6,0.15)', borderRadius:14, padding:'14px 20px', display:'flex', alignItems:'center', gap:14, marginBottom:28 }}>
          <div style={{ fontSize:22 }}><MotusIcon name="trophy" size={22} color={C.gold} /></div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14, fontWeight:700, color:C.t1 }}>Upload a video to earn XP and get coach feedback</div>
            <div style={{ fontSize:12, color:C.t3 }}>Every reviewed video earns +50 XP</div>
          </div>
          <div style={{ background:C.goldDim, border:'1px solid rgba(217,119,6,0.20)', color:C.gold, fontSize:15, fontWeight:800, padding:'7px 18px', borderRadius:20, fontFamily:"'DM Mono',monospace" }}>+50 XP</div>
        </div>

        {/* ── STEP 1: UPLOAD ── */}
        {step === 1 && (
          <div>
            {/* Progress */}
            {(compressing || uploading || uploadProgress > 0) && (
              <div style={{ background:C.card, border:`1px solid rgba(37,99,235,0.15)`, borderRadius:16, padding:'22px 24px', marginBottom:18 }}>
                <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:14 }}>
                  <div style={{ width:56, height:40, borderRadius:9, background:C.card2, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>{(compressing || uploading) ? <MotusSpinner size={24} /> : <MotusIcon name="film" size={22} color={C.t2} />}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:C.t1 }}>{file?.name || 'Video'}</div>
                    <div style={{ fontSize:12, color:C.t3 }}>
                      {fileSizeDisplay}
                      {compressionSavings && <span style={{ color:C.green, fontWeight:700 }}> · Saved {Math.round((1 - compressionSavings.ratio) * 100)}%</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ flex:1, height:8, background:C.card3, borderRadius:8, overflow:'hidden' }}>
                    <div style={{ height:'100%', borderRadius:8, background: `linear-gradient(90deg,${C.blue},${C.purple})`, width:`${compressing ? compressionProgress * 0.3 : 30 + uploadProgress * 0.7}%`, transition:'width .3s' }} />
                  </div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:13, color: C.blue, fontWeight:700, width:38, textAlign:'right' }}>{Math.round(compressing ? compressionProgress * 0.3 : 30 + uploadProgress * 0.7)}%</div>
                </div>
                <div style={{ fontSize:12, color: uploadFailed ? C.orange : C.t3, marginTop:8 }}>{compressing ? 'Preparing video…' : uploadStatus}</div>
                {/* §17/§18 — a durable failure state with an explicit retry on the SAME videoId. */}
                {uploadFailed && !uploading && (
                  <button onClick={retryUpload} style={{ marginTop:10, background:C.orange, color:'#fff', border:'none', borderRadius:9, padding:'8px 16px', fontFamily:font, fontSize:13, fontWeight:700, cursor:'pointer' }}>Retry upload</button>
                )}
              </div>
            )}

            {/* Drop zone */}
            <div onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.blue; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = 'rgba(37,99,235,0.20)'; }}
              onDrop={e => { e.preventDefault(); if (e.dataTransfer.files[0]) handleFileSelect(e.dataTransfer.files[0]); }}
              style={{ border:'2px dashed rgba(37,99,235,0.20)', borderRadius:20, background:C.card, padding:'64px 40px', textAlign:'center', cursor:'pointer', transition:'all .22s' }}>
              <input ref={fileInputRef} type="file" accept="video/*" style={{ display:'none' }} onChange={e => { if (e.target.files[0]) handleFileSelect(e.target.files[0]); }} />
              <div style={{ width:76, height:76, borderRadius:20, background:C.blueDim, border:'1px solid rgba(37,99,235,0.15)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 18px' }}><MotusIcon name="upload" size={32} color={C.blue} /></div>
              <div style={{ fontFamily:fontD, fontSize:30, letterSpacing:1.5, color:C.t1, marginBottom:6 }}>Drop your video here</div>
              <div style={{ fontSize:14, color:C.t3, marginBottom:22 }}>Drag & drop, or choose a file from your device</div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
                <MotusButton variant="primary" icon={<MotusIcon name="folder" size={15} color="#fff" />} onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>Choose File</MotusButton>
                <MotusButton variant="secondary" icon={<MotusIcon name="camera" size={15} />} onClick={e => e.stopPropagation()}>Record Now</MotusButton>
              </div>
              <div style={{ fontSize:11, color:C.t3, marginTop:16 }}>MP4 · MOV · AVI · Max 2 GB{canCompress() ? ' · Auto-compressed' : ''}</div>
            </div>
          </div>
        )}

        {/* ── STEP 2: DETAILS ── */}
        {step === 2 && (
          <div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:28 }}>
              {/* LEFT */}
              <div>
                {videoPreview && (
                  <div style={{ background:C.card, border:`1px solid rgba(37,99,235,0.12)`, borderRadius:16, overflow:'hidden', marginBottom:20 }}>
                    <video src={videoPreview} style={{ width:'100%', height:168, objectFit:'cover' }} muted playsInline onLoadedData={e => { e.target.currentTime = 1; }} />
                    <div style={{ padding:'12px 16px' }}>
                      <div style={{ fontSize:14, fontWeight:700, color:C.t1 }}>{file?.name}</div>
                      <div style={{ fontSize:12, color:C.t3 }}>{fileSizeDisplay}</div>
                    </div>
                  </div>
                )}
                <FG label="Video Title"><input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Parallel Turns — Blue Run" style={iS} /></FG>
                <FG label="Sport">
                  <select value={sport} onChange={e => { setSport(e.target.value); setFocusTags([]); }} style={iS}>
                    <option value="ski">Skiing</option>
                    <option value="snowboard">Snowboard</option>
                  </select>
                </FG>
                <FG label="What would you like feedback on?"><textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe what you were working on…" style={{ ...iS, minHeight:96, resize:'none', lineHeight:1.6 }} /></FG>
              </div>

              {/* RIGHT */}
              <div>
                {/* Sport-specific focus areas grouped by level */}
                <FG label={`Focus Areas — ${SPORT_FOCUS_AREAS[sport]?.label || 'Skiing'}`}>
                  {Object.entries(groupedAreas).map(([levelLabel, areas]) => (
                    <div key={levelLabel} style={{ marginBottom:12 }}>
                      <div style={{ fontSize:10, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:C.t3, marginBottom:6 }}>{levelLabel}</div>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                        {areas.map(a => (
                          <button key={a.key} onClick={() => toggleTag(a.key)} className={`m-chip${focusTags.includes(a.key) ? ' selected' : ''}`}>{a.label}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </FG>

                <FG label="How challenging did this feel?">
                  <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                    {['Easy','Moderate','Challenging','Very Hard'].map(d => (
                      <button key={d} onClick={() => setDifficulty(d)} className={`m-chip${difficulty === d ? ' selected' : ''}`}>{d}</button>
                    ))}
                  </div>
                </FG>

                {/* Coach selector — Pass 72: "First available coach" only. A validated specific-coach picker
                    needs org-scoped instructor data students can't securely read yet (deferred). */}
                <FG label="Assign to Coach">
                  <CoachOpt name="First Available Coach" meta="A coach in your organisation will pick up your video" initials="ANY" bg={`linear-gradient(135deg,${C.orange},#CC4A10)`} speed="Fastest" speedColor={C.green} selected={selectedCoach === 'any'} onClick={() => setSelectedCoach('any')} />
                  {!orgId && (
                    <div style={{ fontSize:12, color:C.orange, padding:'12px 0' }}>Your account isn’t linked to an organisation yet, so this video can’t be sent for review until that’s set up.</div>
                  )}
                  {uploadStatus && !uploading && (
                    <div role="alert" style={{ fontSize:12, color:C.orange, padding:'8px 0' }}>{uploadStatus}</div>
                  )}
                </FG>

                {/* Tips */}
                <div style={{ background:C.card2, border:`1px solid ${C.border}`, borderRadius:14, padding:'16px 18px', marginTop:18 }}>
                  <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:C.t3, marginBottom:12, display:'flex', alignItems:'center', gap:6 }}><MotusIcon name="lightbulb" size={13} color={C.t3} /> Tips for better feedback</div>
                  {['Film from below and to the side','Include 3–5 full turns','Good lighting — avoid shooting into sun','Normal speed is fine — coach will slow down'].map((t, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:9, marginBottom:7, fontSize:12, color:C.t2, lineHeight:1.5 }}>
                      <MotusIcon name="check" size={14} color={C.green} style={{ marginTop:1 }} />{t}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', marginTop:28 }}>
              <MotusButton variant="secondary" onClick={() => setStep(1)}>← Back</MotusButton>
              <MotusButton variant="success" size="lg" loading={submitting} icon={<MotusIcon name="mountain" size={16} color="#fff" />} onClick={handleSubmit}>Submit for Coach Review →</MotusButton>
            </div>
          </div>
        )}

        {/* ── STEP 3: SUCCESS ── */}
        {step === 3 && (
          <div>
            <div style={{ background:'linear-gradient(135deg,#F0FFF4,#ECFDF5)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:20, padding:'52px 40px', textAlign:'center', marginBottom:20 }}>
              <div style={{ marginBottom:18 }}><MotusIcon name="award" size={56} color={C.green} /></div>
              <div style={{ fontFamily:fontD, fontSize:44, letterSpacing:2, color:C.t1, marginBottom:8 }}>Video Submitted!</div>
              <div style={{ fontSize:14, color:C.t2, lineHeight:1.7, maxWidth:520, margin:'0 auto 22px' }}>Your video is now with your coach. You'll get a notification when feedback is ready.</div>
              <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:C.goldDim, border:'1px solid rgba(217,119,6,0.20)', color:C.gold, fontSize:17, fontWeight:800, padding:'10px 24px', borderRadius:22, marginBottom:26, fontFamily:"'DM Mono',monospace" }}><MotusIcon name="trophy" size={18} color={C.gold} /> +50 XP Earned</div>
              <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
                <MotusButton variant="primary" icon={<MotusIcon name="barChart" size={15} color="#fff" />} onClick={() => navigate('/dashboard/progress')}>View Progress</MotusButton>
                <MotusButton variant="primary" icon={<MotusIcon name="upload" size={15} color="#fff" />} onClick={() => { setStep(1); setFile(null); setVideoPreview(null); setUploadProgress(0); setCompressedFile(null); setCompressionSavings(null); }}>Upload Another</MotusButton>
                <MotusButton variant="secondary" onClick={() => navigate('/dashboard')}>Dashboard</MotusButton>
              </div>
            </div>

            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:'24px 26px' }}>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:C.t3, marginBottom:16 }}>What happens next</div>
              <WN n={1} c={C.blue} d={C.blueDim} t="Coach receives your video" s="Your coach is notified instantly with your notes and focus areas." />
              <WN n={2} c={C.green} d={C.greenDim} t="Timestamped feedback" s="Coach adds comments using detect → cause → effect → correct → train." />
              <WN n={3} c={C.orange} d={C.orangeDim} t="You're notified" s="Full review with drills and message thread in My Videos." />
              <WN n={4} c={C.green} d={C.greenDim} t="Skill profile updates" s="Progress tracked over time with recurring patterns identified." last />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ── Helpers ──
function StepDot({n,s}){const done=s>n,act=s===n;return<div style={{display:'flex',alignItems:'center',gap:10}}><div style={{width:38,height:38,borderRadius:'50%',background:done?C.green:act?C.blue:C.card2,border:done||act?'none':`1px solid ${C.border}`,color:done||act?'white':C.t3,boxShadow:done?'0 0 14px rgba(34,197,94,0.20)':act?'0 0 16px rgba(37,99,235,0.20)':'none',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:800}}>{done?'✓':n}</div><div style={{fontSize:13,fontWeight:600,color:done?C.green:act?C.t1:C.t3}}>{['','Upload','Details','Submitted'][n]}</div></div>;}
function StepLine({done,active}){return<div style={{flex:1,height:2,maxWidth:80,margin:'0 10px',background:done?C.green:active?`linear-gradient(90deg,${C.green},${C.blue})`:C.border}} />;}
function FG({label,children}){return<div style={{marginBottom:18}}><label style={{fontSize:11,fontWeight:700,letterSpacing:1.5,textTransform:'uppercase',color:C.t3,marginBottom:8,display:'block'}}>{label}</label>{children}</div>;}
const iS = {width:'100%',background:C.card2,border:`1px solid ${C.border}`,borderRadius:11,padding:'12px 15px',fontFamily:font,fontSize:14,color:C.t1,outline:'none'};
function WN({n,c,d,t,s,last}){return<div style={{display:'flex',gap:16,padding:'13px 0',borderBottom:last?'none':`1px solid ${C.border}`,alignItems:'flex-start'}}><div style={{width:30,height:30,borderRadius:'50%',background:d,border:`1px solid ${c}20`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:c,flexShrink:0}}>{n}</div><div><div style={{fontSize:14,fontWeight:700,color:C.t1,marginBottom:3}}>{t}</div><div style={{fontSize:12,color:C.t3,lineHeight:1.55}}>{s}</div></div></div>;}

function CoachOpt({name,meta,initials,bg,speed,speedColor,selected,onClick}){
  return(
    <div onClick={onClick} style={{display:'flex',alignItems:'center',gap:12,background:selected?C.blueDim:C.card2,border:`1px solid ${selected?'rgba(37,99,235,0.25)':C.border}`,borderRadius:12,padding:'12px 14px',cursor:'pointer',transition:'all .18s',marginBottom:8}}>
      <div style={{width:40,height:40,borderRadius:'50%',background:bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:initials.length>2?12:15,fontWeight:700,color:'white',flexShrink:0}}>{initials}</div>
      <div style={{flex:1}}>
        <div style={{fontSize:13,fontWeight:700,color:C.t1,marginBottom:2}}>{name}</div>
        <div style={{fontSize:11,color:C.t3}}>{meta}</div>
      </div>
      {speed && <div style={{padding:'3px 9px',borderRadius:20,fontSize:10,fontWeight:700,background:C.greenDim,border:'1px solid rgba(34,197,94,0.20)',color:speedColor||C.green,whiteSpace:'nowrap'}}>{speed}</div>}
      <div style={{width:22,height:22,borderRadius:'50%',border:`2px solid ${selected?C.blue:C.border}`,background:selected?C.blue:'transparent',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,color:selected?'white':'transparent',flexShrink:0}}>✓</div>
    </div>
  );
}

export default VideoUploadPage;
